import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { INSTAGRAM_URL, MAPS_URL, WHATSAPP_URL } from '@/data/constants';
import { InstagramIcon, WhatsAppIcon } from '@/components/icons';

const MAP_COORDS = { lat: 34.1287214251452, lon: 35.6522176404791 };

function LeafletMap() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: [MAP_COORDS.lat, MAP_COORDS.lon],
      zoom: 16,
      scrollWheelZoom: false,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    const icon = L.icon({
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });

    L.marker([MAP_COORDS.lat, MAP_COORDS.lon], { icon })
      .addTo(map)
      .bindPopup('<strong>HUB30</strong><br>Jbeil, Lebanon')
      .openPopup();

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  return <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }} />;
}

const HOURS = [
  { day: 'Monday – Friday', time: '5:45 AM – 10:00 PM', highlight: false, closed: false },
  { day: 'Saturday',        time: '7:00 AM – 3:00 PM',  highlight: true,  closed: false },
  { day: 'Sunday',          time: 'Closed',              highlight: false, closed: true  },
] as const;

export default function Contact() {
  return (
    <section id="contact" className="bg-[#0d0d0d] py-16 lg:py-32 px-4 lg:px-8 xl:px-16">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <span data-animate className="inline-flex items-center justify-center gap-2 text-[#14B8A6] text-xs tracking-widest font-medium mb-4">
            <span className="w-4 h-px bg-[#14B8A6]" />
            GET IN TOUCH
            <span className="w-4 h-px bg-[#14B8A6]" />
          </span>
          <h2 data-animate data-delay="1" className="text-white text-4xl lg:text-6xl font-bold mb-4">Contact Us</h2>
          <p data-animate data-delay="2" className="text-gray-400 text-base lg:text-lg max-w-xl mx-auto">
            Drop by for a tour or reach out to get started.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Map */}
          <div data-animate="left" className="relative rounded-3xl overflow-hidden h-72 lg:h-auto lg:min-h-[480px] bg-[#1a1a1a] border border-white/[0.06]">
            <LeafletMap />
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-4 right-4 flex items-center gap-2 bg-[#14B8A6] hover:bg-[#0d9488] text-white text-xs font-semibold px-4 py-2.5 rounded-full shadow-lg transition-all duration-200 hover:shadow-[0_0_16px_rgba(20,184,166,0.4)]"
            >
              <i className="ri-map-pin-2-line" />
              Open in Google Maps
            </a>
          </div>

          {/* Info Cards */}
          <div className="flex flex-col gap-4">
            {/* Location */}
            <a
              data-animate
              data-delay="1"
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-[#141414] border border-white/[0.07] rounded-2xl p-5 lg:p-6 hover:border-[#14B8A6]/30 hover:bg-[#181818] transition-all duration-300 group"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#14B8A6]/10 border border-[#14B8A6]/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#14B8A6]/20 transition-colors">
                  <i className="ri-map-pin-line text-[#14B8A6] text-lg" />
                </div>
                <div>
                  <h3 className="text-white text-base font-bold mb-1">Location</h3>
                  <p className="text-gray-400 text-sm">Jbeil, Lebanon</p>
                  <span className="text-[#14B8A6] text-xs mt-1.5 inline-block font-medium">Get directions →</span>
                </div>
              </div>
            </a>

            {/* Contact */}
            <div
              data-animate
              data-delay="2"
              className="bg-[#141414] border border-white/[0.07] rounded-2xl p-5 lg:p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#14B8A6]/10 border border-[#14B8A6]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-[#14B8A6] text-lg" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white text-base font-bold mb-3">Contact</h3>
                  <div className="space-y-2.5">
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2.5 text-gray-400 hover:text-[#25D366] transition-colors group"
                    >
                      <WhatsAppIcon className="text-sm group-hover:scale-110 transition-transform" />
                      <span className="text-sm">+961 71 077 545</span>
                    </a>
                    <a
                      href={INSTAGRAM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2.5 text-gray-400 hover:text-[#14B8A6] transition-colors group"
                    >
                      <InstagramIcon className="text-sm group-hover:scale-110 transition-transform" />
                      <span className="text-sm">@hub30lb</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Hours */}
            <div
              data-animate
              data-delay="3"
              className="bg-[#141414] border border-white/[0.07] rounded-2xl p-5 lg:p-6 flex-1"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#14B8A6]/10 border border-[#14B8A6]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-[#14B8A6] text-lg" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white text-base font-bold mb-4">Opening Hours</h3>
                  <div className="space-y-3">
                    {HOURS.map(({ day, time, highlight, closed }) => (
                      <div
                        key={day}
                        className={`flex items-center justify-between gap-4 text-xs rounded-lg px-3 py-2 ${
                          highlight
                            ? 'bg-[#14B8A6]/8 border-l-2 border-[#14B8A6]'
                            : closed
                            ? 'opacity-60'
                            : ''
                        }`}
                      >
                        <span className={highlight ? 'text-[#14B8A6] font-medium' : 'text-gray-400'}>
                          {day}
                        </span>
                        <span className={closed ? 'text-red-400' : highlight ? 'text-[#14B8A6] font-medium' : 'text-white'}>
                          {time}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
